import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface LabelDialogProps {
  title: string;
  initialLabel: string;
  initialNumber: string;
  onConfirm: (next: { label: string; number: string }) => void;
  onClose: () => void;
  /** Hide the "Nummer" input — used by the text-element editor. */
  hideNumber?: boolean;
  /** Render the label as a multi-line textarea instead of a single input. */
  multiline?: boolean;
  /** Custom label for the text/label field (defaults to "Name / Label"). */
  labelFieldTitle?: string;
}

/**
 * Generic modal used to edit a label + optional number (replaces window.prompt).
 * Originally built for tactics token rename (opponents/dummies) and reused by
 * the text-element tool. Renders into document.body via portal.
 *
 * Pass `hideNumber` + `multiline` to collapse it into a single-textarea editor.
 */
export function LabelDialog({
  title,
  initialLabel,
  initialNumber,
  onConfirm,
  onClose,
  hideNumber = false,
  multiline = false,
  labelFieldTitle,
}: LabelDialogProps) {
  const [label, setLabel] = useState(initialLabel);
  const [number, setNumber] = useState(initialNumber);
  const numberRef = useRef<HTMLInputElement | null>(null);
  const labelRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    // Focus the number field initially for token edits (most-edited field
    // for opponents/dummies). When the number is hidden (text element),
    // jump straight to the label/textarea instead.
    if (hideNumber) {
      labelRef.current?.focus();
    } else {
      (numberRef.current ?? labelRef.current)?.focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, hideNumber]);

  const submit = () => onConfirm({ label: label.trim(), number: number.trim() });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-200 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="glass-floating w-[min(360px,calc(100vw-32px))] rounded-3xl p-4 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-body-sm font-semibold uppercase tracking-wider">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/6 hover:bg-white/10"
          >
            <X className="size-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex flex-col gap-3"
        >
          {!hideNumber && (
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Nummer
              </span>
              <input
                ref={numberRef}
                type="text"
                inputMode="numeric"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                maxLength={3}
                className="h-10 rounded-2xl border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:bg-white/7"
                placeholder="z.B. 9"
              />
            </label>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {labelFieldTitle ?? "Name / Label"}
            </span>
            {multiline ? (
              <textarea
                ref={labelRef as React.RefObject<HTMLTextAreaElement>}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                rows={4}
                maxLength={500}
                onKeyDown={(e) => {
                  // Enter inserts newline; Cmd/Ctrl+Enter submits.
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    submit();
                  }
                }}
                className="min-h-28 resize-y rounded-2xl border border-white/15 bg-white/4 px-3 py-2 text-body-sm leading-snug outline-none focus:border-pitch-lime/60 focus:bg-white/7"
                placeholder="Text eingeben…"
              />
            ) : (
              <input
                ref={labelRef as React.RefObject<HTMLInputElement>}
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={40}
                className="h-10 rounded-2xl border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:bg-white/7"
                placeholder="optional"
              />
            )}
          </label>

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-full border border-white/15 bg-white/4 px-4 text-body-sm hover:bg-white/10"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="h-10 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] shadow-glow hover:brightness-110"
            >
              Übernehmen
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
